/**
 * Web change review: the twin of `HealthChangeReview.tsx`. What "Review N
 * suggestions" and a single suggestion row open.
 *
 *   ‹ Overview
 *   ♥ Your changes are ready
 *   Keep the changes you like. Edit anything.
 *   [x] Make your summary more specific        ▾ Compare & edit
 *       why · Original · Suggested (editable) · what the change keeps
 *   ...
 *   3 selected · Original kept
 *   [ Save 3 changes ]  (onboarding: Use in my new CV)
 *   Keep original
 *
 * Selection is independent of expansion: the first change opens on arrival
 * (the only one, for a single suggestion) and the others open on demand. The
 * user's text is never lost by collapsing or going back; the host keeps it.
 * An error names its change: that card opens and its field takes focus.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { HealthHeart } from './HealthHeart';
import { getChangeLabelKey, getFindingChangeKey, getFindingWhyKey } from './cv-health-copy';
import type { HealthChangeReviewItem, HealthChangeReviewProps } from './health-props';

export type { HealthChangeReviewProps };

function ChangeCard({
  item,
  open,
  onToggleOpen,
  onToggle,
  onTextChange,
  invalid,
  focusField,
  focusNonce,
  disabled,
}: {
  item: HealthChangeReviewItem;
  open: boolean;
  onToggleOpen: () => void;
  onToggle: () => void;
  onTextChange: (text: string) => void;
  invalid: boolean;
  focusField: boolean;
  focusNonce: number;
  disabled: boolean;
}) {
  const { t } = useTranslation('common');
  const reactId = useId().replace(/:/g, '');
  const panelId = `tabbio-health-change-${reactId}`;
  const fieldId = `tabbio-health-change-field-${reactId}`;
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const { finding } = item;
  const proposal = finding.proposal;
  const label = t(getChangeLabelKey(finding));
  const isLink = proposal?.target.kind === 'link';

  useEffect(() => {
    if (focusField && open) fieldRef.current?.focus();
  }, [focusField, focusNonce, open]);

  if (!proposal) return null;
  return (
    <article className="tabbio-health-change-card" data-testid="cv-health-change">
      <div className="tabbio-health-change-head">
        <label className="tabbio-health-include">
          <input type="checkbox" checked={item.include} onChange={onToggle} disabled={disabled} />
          <span>{label}</span>
        </label>
        <button
          type="button"
          className="tabbio-health-change-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggleOpen}
        >
          <span>{t('cvHealth.review.compare')}</span>
          <span className="tabbio-health-sr">: {label}</span>
          <span className={`tabbio-health-chevron${open ? ' is-open' : ''}`}>
            <MaterialSymbol name="expand_more" size={18} />
          </span>
        </button>
      </div>
      <div
        id={panelId}
        className={`tabbio-health-disclosure${open ? ' is-open' : ''}`}
        aria-hidden={!open}
        inert={!open}
      >
        <div>
          <div className="tabbio-health-change-body">
            <p className="tabbio-health-change-why">{t(getFindingWhyKey(finding))}</p>
            <div className="tabbio-health-before">
              <span>{t('cvHealth.review.original')}</span>
              <p className="ph-no-capture" dir="auto">
                {proposal.before || t('cvHealth.actions.nothingYet')}
              </p>
            </div>
            <label className="tabbio-health-field-label" htmlFor={fieldId}>
              {t('cvHealth.review.suggested')}
            </label>
            <textarea
              ref={fieldRef}
              id={fieldId}
              className="tabbio-health-textarea ph-no-capture"
              dir="auto"
              rows={isLink ? 2 : 4}
              maxLength={1200}
              value={item.text}
              aria-invalid={invalid || undefined}
              disabled={disabled}
              onChange={(event) => onTextChange(event.target.value)}
            />
            <p className="tabbio-health-small">{t(getFindingChangeKey(finding))}</p>
          </div>
        </div>
      </div>
    </article>
  );
}

export function HealthChangeReview({
  items,
  onToggle,
  onTextChange,
  onApply,
  onKeepOriginal,
  onBack,
  applyLabel,
  saving,
  error,
  errorFindingKey,
  errorNonce = 0,
  notice,
  otherCount = 0,
  onShowOthers,
}: HealthChangeReviewProps) {
  const { t } = useTranslation('common');
  const [openKeys, setOpenKeys] = useState<ReadonlySet<string>>(
    () => new Set(items[0] ? [items[0].finding.key] : []),
  );
  const selected = items.filter((item) => item.include).length;

  // An error about one change opens it, so its field can take focus.
  useEffect(() => {
    if (!errorFindingKey) return;
    setOpenKeys((current) => (current.has(errorFindingKey) ? current : new Set([...current, errorFindingKey])));
  }, [errorFindingKey, errorNonce]);

  const toggleOpen = (key: string) =>
    setOpenKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <section className="tabbio-health tabbio-health-change-review">
      <button type="button" className="tabbio-health-back" onClick={onBack} disabled={saving}>
        <MaterialSymbol name="chevron_left" size={18} className="tabbio-rtl-flip" />
        {t('cvHealth.report.overview')}
      </button>
      <div className="tabbio-health-ready-title">
        <HealthHeart size={22} state={saving ? 'working' : 'resting'} />
        <h2>{t('cvHealth.review.heading')}</h2>
      </div>
      <p className="tabbio-health-lead">{t('cvHealth.review.lead')}</p>
      {notice ? (
        <p className="tabbio-health-review-notice" role="status">
          {notice}
        </p>
      ) : null}

      <div className="tabbio-health-change-list">
        {items.map((item) => (
          <ChangeCard
            key={item.finding.key}
            item={item}
            open={openKeys.has(item.finding.key)}
            onToggleOpen={() => toggleOpen(item.finding.key)}
            onToggle={() => onToggle(item.finding.key)}
            onTextChange={(text) => onTextChange(item.finding.key, text)}
            invalid={Boolean(error) && errorFindingKey === item.finding.key}
            focusField={Boolean(error) && errorFindingKey === item.finding.key}
            focusNonce={errorNonce}
            disabled={saving}
          />
        ))}
      </div>
      {otherCount > 0 ? (
        <p className="tabbio-health-review-others">
          {t('cvHealth.review.others', { count: otherCount })}{' '}
          {onShowOthers ? (
            <button
              type="button"
              className="tabbio-health-review-others-link"
              onClick={onShowOthers}
              disabled={saving}
            >
              {t('cvHealth.review.seeOthers')}
            </button>
          ) : null}
        </p>
      ) : null}

      <div className="tabbio-health-review-actions">
        <p className="tabbio-health-small is-tight" role="status">
          {t('cvHealth.review.selected', { count: selected })}
        </p>
        {error ? (
          <p className="tabbio-health-error" role="alert">
            {error}
          </p>
        ) : null}
        <button
          type="button"
          className="tabbio-health-btn is-primary is-block"
          onClick={onApply}
          disabled={selected === 0 || saving}
          aria-busy={saving || undefined}
        >
          {saving ? t('cvHealth.review.saving') : applyLabel}
        </button>
        <button
          type="button"
          className="tabbio-health-btn is-text is-block"
          onClick={onKeepOriginal}
          disabled={saving}
        >
          {t('cvHealth.review.keepOriginal')}
        </button>
      </div>
    </section>
  );
}

export default HealthChangeReview;
